import { GraphQLClient } from 'graphql-request';

// Add type definitions for Vite's import.meta.env
interface ImportMetaEnv {
  readonly VITE_SHOPIFY_DOMAIN: string;
  readonly VITE_SHOPIFY_TOKEN: string;
}

interface ImportMeta {
  readonly env: ImportMetaEnv;
}

const SHOPIFY_DOMAIN = import.meta.env.VITE_SHOPIFY_DOMAIN;
const SHOPIFY_STOREFRONT_TOKEN = import.meta.env.VITE_SHOPIFY_TOKEN;

const client = new GraphQLClient(
  `https://${SHOPIFY_DOMAIN}/api/2024-01/graphql.json`,
  {
    headers: {
      'X-Shopify-Storefront-Access-Token': SHOPIFY_STOREFRONT_TOKEN,
      'Content-Type': 'application/json',
    },
  }
);

// GraphQL Queries
const PRODUCTS_QUERY = `
  query Products {
    products(first: 50) {
      edges {
        node {
          id
          title
          handle
          description
          images(first: 1) {
            edges {
              node {
                url
                altText
              }
            }
          }
          priceRange {
            minVariantPrice {
              amount
              currencyCode
            }
          }
          variants(first: 1) {
            edges {
              node {
                id
                title
                price {
                  amount
                  currencyCode
                }
              }
            }
          }
        }
      }
    }
  }
`;

const PRODUCT_BY_HANDLE_QUERY = `
  query ProductByHandle($handle: String!) {
    productByHandle(handle: $handle) {
      id
      title
      handle
      description
      images(first: 5) {
        edges {
          node {
            url
            altText
          }
        }
      }
      priceRange {
        minVariantPrice {
          amount
          currencyCode
        }
      }
      variants(first: 10) {
        edges {
          node {
            id
            title
            price {
              amount
              currencyCode
            }
          }
        }
      }
    }
  }
`;

const CREATE_CART_MUTATION = `
  mutation CreateCart($input: CartInput!) {
    cartCreate(input: $input) {
      cart {
        id
        checkoutUrl
        lines(first: 10) {
          edges {
            node {
              id
              quantity
              merchandise {
                ... on ProductVariant {
                  id
                  title
                  price {
                    amount
                    currencyCode
                  }
                }
              }
            }
          }
        }
        buyerIdentity {
          countryCode
          email
          phone
        }
      }
      userErrors {
        field
        message
      }
    }
  }
`;

export interface Product {
  id: string;
  title: string;
  handle: string;
  description: string;
  images: {
    edges: Array<{
      node: {
        url: string;
        altText: string | null;
      };
    }>;
  };
  priceRange: {
    minVariantPrice: {
      amount: string;
      currencyCode: string;
    };
  };
  variants: {
    edges: Array<{
      node: {
        id: string;
        title: string;
        price: {
          amount: string;
          currencyCode: string;
        };
      };
    }>;
  };
}

interface ProductsResponse {
  products: {
    edges: Array<{
      node: Product;
    }>;
  };
}

interface ProductByHandleResponse {
  productByHandle: Product;
}

interface CartCreateResponse {
  cartCreate: {
    cart: {
      id: string;
      checkoutUrl: string;
      lines: {
        edges: Array<{
          node: {
            id: string;
            quantity: number;
            merchandise: {
              id: string;
              title: string;
              price: {
                amount: string;
                currencyCode: string;
              };
            };
          };
        }>;
      };
      buyerIdentity: {
        countryCode: string;
        email: string;
        phone: string;
      };
    };
    userErrors: Array<{
      field: string;
      message: string;
    }>;
  };
}

export async function getProducts(): Promise<Product[]> {
  try {
    const response = await client.request<ProductsResponse>(PRODUCTS_QUERY);
    return response.products.edges.map(edge => edge.node);
  } catch (error) {
    console.error('Error fetching products:', error);
    throw error;
  }
}

export async function getProductByHandle(handle: string): Promise<Product> {
  try {
    const response = await client.request<ProductByHandleResponse>(PRODUCT_BY_HANDLE_QUERY, { handle });
    return response.productByHandle;
  } catch (error) {
    console.error('Error fetching product:', error);
    throw error;
  }
}

interface CartItem {
  variantId: string;
  quantity: number;
}

export async function createCart(items: CartItem[]) {
  try {
    const response = await client.request<CartCreateResponse>(CREATE_CART_MUTATION, {
      input: {
        lines: items.map(item => ({
          quantity: item.quantity,
          merchandiseId: item.variantId,
          attributes: [
            {
              key: "checkout_type",
              value: "c1"
            }
          ]
        })),
        buyerIdentity: {
          countryCode: "DE", // Default to Germany, can be made dynamic
          deliveryAddressPreferences: {
            address1: "",
            city: "",
            country: "Germany",
            province: "",
            zip: ""
          }
        }
      }
    });

    if (response.cartCreate.userErrors.length > 0) {
      throw new Error(response.cartCreate.userErrors[0].message);
    }

    // Ensure we have a valid checkout URL
    if (!response.cartCreate.cart.checkoutUrl) {
      throw new Error('Failed to generate checkout URL');
    }

    // Add authentication token to checkout URL if needed
    const checkoutUrl = new URL(response.cartCreate.cart.checkoutUrl);
    checkoutUrl.searchParams.set('checkout_type', 'c1');

    return {
      ...response.cartCreate.cart,
      checkoutUrl: checkoutUrl.toString()
    };
  } catch (error) {
    console.error('Error creating cart:', error);
    throw error;
  }
} 